import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Alert } from '@mui/material';
import { EmptyState } from '../components/common/EmptyState';
import { LoadingSkeleton } from '../components/common/LoadingSkeleton';
import { useAuth } from './AuthProvider';

/**
 * Guards `/partner/*` routes.
 *
 * Partner authority is a MEMBERSHIP in a white-label partner — the third
 * authorization surface. It is not a tenant role, so a TENANT_ADMIN does not administer a
 * partner and this guard does not consult roles at all.
 *
 * ⚠️ **A platform administrator who has ENTERED a partner is admitted, and the paragraph
 * that used to be here said the opposite.** It argued that platform authority does not make
 * somebody an administrator OF a partner, *"the server refuses them, and a page that
 * rendered for them would be a page whose every call 403s"* — true when written, and false
 * from the moment `WhiteLabelContextGuard` gained its platform branch. Left in place it
 * would have been worse than no comment: it instructs the next reader to undo the fix. The
 * stale-message shape this tier has already produced twice.
 *
 * Merely HOLDING the capability still admits nobody. `assumedPartner` is set only by
 * `assumePartner`, which is a deliberate act from the platform's partner record — so the
 * client's condition is "somebody named where they are going", which is exactly what the
 * server honours.
 *
 * `me.whiteLabels` is a **routing and display hint only**. The authoritative check is
 * `WhiteLabelContextGuard`, which re-reads the memberships on every request.
 *
 * ⚠️ **This paragraph used to end *"a WL_VIEWER gets an empty array here and is denied"*,
 * and that was the defect rather than the design.** A viewer signed in, `whiteLabels` came
 * back empty, `landingFor` saw no partner authority and sent them to *"You do not belong to
 * a tenant yet"* — told to ask an administrator for access that administrator had just
 * granted them on the People page. Viewers are listed now and reach this console read-only;
 * every non-GET is refused by the guard.
 *
 * The same three behaviours `RequirePlatform` settled on:
 *  - while `/auth/me` is in flight, render a loading state rather than a denial,
 *    so a slow network is not misreported as "no access";
 *  - denial is explicit, not a silent redirect;
 *  - denial does NOT clear the session — the user is authenticated and simply
 *    administers no partner.
 */
export function RequirePartner({ children }: { children: ReactNode }) {
  const { ready, user, assumedPartner, partnerRole } = useAuth();

  if (!ready) return <LoadingSkeleton rows={6} />;
  if (!user) return <Navigate to="/login" replace />;

  /*
   * ⚠️ Truthy, never `!== null`: a context built before this field existed yields
   * `undefined`, which is not null — the guard that already bit once in `useNavAuth`.
   */
  if ((user.whiteLabels ?? []).length === 0 && !assumedPartner) {
    /*
     * ⚠️ **LEAVING MUST NAVIGATE, or it strands the reader on a page they can no longer
     * open.** `exitAssumedPartner` does not route — the reader is standing on
     * `/partner/companies` when they press it — so without this they were left reading
     * *"You do not have access"*, which describes a permissions fault rather than the fact
     * that they just stepped out. Measured on the built page before the fix.
     *
     * The identical defect the tenant surface already records for *Leave tenant*, and the
     * same remedy: a REDIRECT rather than making the button navigate, because it also
     * covers arriving without pressing anything — a bookmark, the back button, a link from
     * a colleague.
     *
     * ⚠️ Only for a PLATFORM administrator, who has somewhere to go. For anybody else the
     * empty state is the right answer and always was: they genuinely administer no partner,
     * and bouncing them to a platform page they also cannot open would be worse. The target
     * is under `/platform`, so it cannot loop back into this guard.
     */
    if (user.isPlatformAdmin) return <Navigate to="/platform/white-labels" replace />;
    return (
      <EmptyState
        title="You do not have access"
        message="This area is for white-label partner administrators. If you think this is wrong, contact your platform operator."
      />
    );
  }

  /*
   * ⚠️ **SAID ONCE, HERE, because this wrapper is the only thing every `/partner/*` page has
   * in common.** Putting it on each page is five chances to forget, and a page added later
   * would carry no notice at all — a viewer meeting a refused save with nothing on screen
   * explaining why is the state this whole change exists to end.
   *
   * It is an EXPLANATION rather than a warning: viewer access is a perfectly ordinary thing
   * for a partner to grant, and amber on every page all day teaches a reader to skip it.
   */
  if (partnerRole === 'WL_VIEWER') {
    return (
      <>
        <Alert severity="info" icon={false} sx={{ mb: 2 }}>
          <strong>You have viewer access.</strong> You can read this partner’s companies,
          brand and people. Changing them, and opening a company, need administrator access —
          ask an administrator on the People page.
        </Alert>
        {children}
      </>
    );
  }

  return <>{children}</>;
}
